import { ChatWidgetConfig, ResolvedConfig, ChatMessage } from './types';
import { ChatService } from './api/chat.service';
import { widgetStyles } from './styles/widget.css';
import { icons } from './ui/icons';

/**
 * Generate unique ID
 */
function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Generate unique user ID for anonymous users
 */
function generateUserId(): string {
  const key = 'chat_widget_user_id';
  try {
    const stored = localStorage.getItem(key);
    if (stored) return stored;
    const id = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(key, id);
    return id;
  } catch {
    return `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

/**
 * Simple markdown parser for chat messages
 */
function parseMarkdown(text: string): string {
  return text
    // Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Code blocks
    .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Bold
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
    // Line breaks
    .replace(/\n/g, '<br>');
}

/**
 * Format timestamp for display
 */
function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Default configuration values
 */
const DEFAULT_CONFIG: Omit<ResolvedConfig, 'apiUrl'> = {
  apiKey: null,

  // Branding
  title: 'Chat',
  subtitle: 'We typically reply within minutes',
  showPoweredBy: false,
  poweredByText: 'Powered by n8n',
  poweredByUrl: 'https://n8n.io',

  // Appearance
  position: 'bottom-right',
  theme: 'light',
  primaryColor: '#6366f1',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
  botBubbleColor: '#f3f4f6',
  userBubbleColor: '#6366f1',
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontSize: 14,
  borderRadius: 16,
  width: 380,
  height: 600,

  // Button
  buttonSize: 60,
  buttonIcon: null,
  closeIcon: null,
  buttonOffset: { x: 24, y: 24 },

  // Messages
  welcomeMessage: null,
  initialPrompts: [],
  placeholder: 'Type a message...',
  errorMessage: 'Sorry, something went wrong. Please try again.',
  showTimestamps: true,
  showBotAvatar: true,
  botAvatar: '🤖',
  botName: 'Assistant',

  // Behavior
  startOpen: false,
  autoFocus: true,
  soundEnabled: false,
  soundUrl: null,
  persistHistory: true,
  maxHistoryLength: 50,
  autoScroll: true,
  sendOnEnter: true,

  // Header
  showCloseButton: true,
  showHeader: true,
  headerHeight: 70,

  // User
  user: {
    id: generateUserId(),
    name: null,
    email: null,
    avatar: null
  },

  metadata: {},

  // Container
  container: null,
  embedded: false,

  // Callbacks
  onOpen: null,
  onClose: null,
  onMessageSent: null,
  onMessageReceived: null,
  onError: null
};

const STORAGE_KEY = 'chat_widget_history';

/**
 * Main Chat Widget Class
 * Uses Shadow DOM for style isolation
 */
export class Widget {
  private config: ResolvedConfig;
  private chatService: ChatService;
  private container: HTMLElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private isOpen = false;
  private isExpanded = false;
  private isLoading = false;
  private messages: ChatMessage[] = [];
  private initialized = false;
  private audio: HTMLAudioElement | null = null;

  // DOM references
  private panelEl: HTMLElement | null = null;
  private messagesEl: HTMLElement | null = null;
  private inputEl: HTMLTextAreaElement | null = null;
  private toggleBtn: HTMLElement | null = null;
  private sendBtn: HTMLButtonElement | null = null;

  constructor(config: ChatWidgetConfig) {
    this.config = this.resolveConfig(config);
    this.chatService = new ChatService(this.config);

    // Initialize audio for notifications
    if (this.config.soundEnabled) {
      this.audio = new Audio(this.config.soundUrl || 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp+cmZiXmJucn6Kko6GfnZqYl5aWl5mbnZ+goaKioqGgn56dnJuamZiYmJmbnZ+goaKio6OjoqGgnp2bmZiXl5eYmpydnp+goaGhoaGgnp2bmpmYl5eYmZqbnZ6foKChoaGhoaCfnp2cmpmYl5eXmJmam5ydn5+goKChoaGgnp6dnJuamZiXl5iYmZqbnJ2en5+goKGhoaCfnp2cm5qZmJeXl5iZmpucnZ6fn6ChoaGgoJ+enZybmpmYl5eXmJmam5ydnp+foKChoaCgn56dnJuamZiXl5eYmZqbnJ2en5+goKGhoKCfnp2cm5qZmJeXl5iZmpucnZ6fn6ChoaGgoJ+enZybmpmYl5eXmJmam5ydnp+foKChoaCgn56dnJuamZiYmJiZmpucnZ6en5+goKCgoJ+enp2cm5qZmZiYmJmam5ucnZ6en5+foKCgn5+enZ2cm5qamZiYmJmam5ucnZ6en5+fn5+fn56enZycm5qamZmYmJmam5ucnZ2enp+fn5+fnp6enZ2cm5uamZmYmJmam5ucnJ2enp6fn5+fnp6enZ2cm5uamZmYmJmam5ucnJ2enp6enp6enp6dnZ2cm5uamZmZmZmam5ucnJ2dnp6enp6enZ2dnZycm5uampmZmZmam5ucnJ2dnZ6enp6dnZ2dnJycm5uampqZmZmam5ucnJydnZ2dnZ2dnZ2cnJycm5uampmZmZmam5ucnJydnZ2dnZ2dnZ2cnJybm5uampqZmZqam5ucnJycnZ2dnZ2dnZ2cnJybm5ubmpmampqam5ucnJycnJ2dnZ2dnZycnJybm5ubmpqampqam5ucnJycnJ2dnZ2dnJycm5ubm5uampqampqbm5ycnJycnJ2dnZ2cnJybm5ubm5qampqampubm5ycnJycnJydnZycnJubm5ubmpqampqam5ubm5ycnJycnJycnJycm5ubm5uampqampqbm5ubm5ycnJycnJycm5ubm5ubmpqampqam5ubm5ubm5ycnJycnJybm5ubm5uampqampubm5ubm5ucnJycnJybm5ubm5ubmpqampqbm5ubm5ubm5ycnJybm5ubm5ubmpqampubm5ubm5ubm5ubm5ubm5ubm5ubm5qam5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ub');
    }
  }

  /**
   * Resolve container element from string selector or element
   */
  private resolveContainer(container?: HTMLElement | string): HTMLElement | null {
    if (!container) return null;
    if (typeof container === 'string') {
      return document.querySelector(container);
    }
    return container;
  }

  /**
   * Resolve user config with defaults
   */
  private resolveConfig(config: ChatWidgetConfig): ResolvedConfig {
    if (!config.apiUrl) {
      throw new Error('[ChatWidget] apiUrl is required');
    }

    const primaryColor = config.primaryColor ?? DEFAULT_CONFIG.primaryColor;

    return {
      apiUrl: config.apiUrl,
      apiKey: config.apiKey ?? DEFAULT_CONFIG.apiKey,

      // Branding
      title: config.title ?? DEFAULT_CONFIG.title,
      subtitle: config.subtitle ?? DEFAULT_CONFIG.subtitle,
      showPoweredBy: config.showPoweredBy ?? DEFAULT_CONFIG.showPoweredBy,
      poweredByText: config.poweredByText ?? DEFAULT_CONFIG.poweredByText,
      poweredByUrl: config.poweredByUrl ?? DEFAULT_CONFIG.poweredByUrl,

      // Appearance
      position: config.position ?? DEFAULT_CONFIG.position,
      theme: config.theme ?? DEFAULT_CONFIG.theme,
      primaryColor,
      backgroundColor: config.backgroundColor ?? DEFAULT_CONFIG.backgroundColor,
      textColor: config.textColor ?? DEFAULT_CONFIG.textColor,
      botBubbleColor: config.botBubbleColor ?? DEFAULT_CONFIG.botBubbleColor,
      userBubbleColor: config.userBubbleColor ?? primaryColor,
      fontFamily: config.fontFamily ?? DEFAULT_CONFIG.fontFamily,
      fontSize: config.fontSize ?? DEFAULT_CONFIG.fontSize,
      borderRadius: config.borderRadius ?? DEFAULT_CONFIG.borderRadius,
      width: config.width ?? DEFAULT_CONFIG.width,
      height: config.height ?? DEFAULT_CONFIG.height,

      // Button
      buttonSize: config.buttonSize ?? DEFAULT_CONFIG.buttonSize,
      buttonIcon: config.buttonIcon ?? DEFAULT_CONFIG.buttonIcon,
      closeIcon: config.closeIcon ?? DEFAULT_CONFIG.closeIcon,
      buttonOffset: {
        x: config.buttonOffset?.x ?? DEFAULT_CONFIG.buttonOffset.x,
        y: config.buttonOffset?.y ?? DEFAULT_CONFIG.buttonOffset.y
      },

      // Messages
      welcomeMessage: config.welcomeMessage ?? DEFAULT_CONFIG.welcomeMessage,
      initialPrompts: config.initialPrompts ?? DEFAULT_CONFIG.initialPrompts,
      placeholder: config.placeholder ?? DEFAULT_CONFIG.placeholder,
      errorMessage: config.errorMessage ?? DEFAULT_CONFIG.errorMessage,
      showTimestamps: config.showTimestamps ?? DEFAULT_CONFIG.showTimestamps,
      showBotAvatar: config.showBotAvatar ?? DEFAULT_CONFIG.showBotAvatar,
      botAvatar: config.botAvatar ?? DEFAULT_CONFIG.botAvatar,
      botName: config.botName ?? DEFAULT_CONFIG.botName,

      // Behavior
      startOpen: config.startOpen ?? DEFAULT_CONFIG.startOpen,
      autoFocus: config.autoFocus ?? DEFAULT_CONFIG.autoFocus,
      soundEnabled: config.soundEnabled ?? DEFAULT_CONFIG.soundEnabled,
      soundUrl: config.soundUrl ?? DEFAULT_CONFIG.soundUrl,
      persistHistory: config.persistHistory ?? DEFAULT_CONFIG.persistHistory,
      maxHistoryLength: config.maxHistoryLength ?? DEFAULT_CONFIG.maxHistoryLength,
      autoScroll: config.autoScroll ?? DEFAULT_CONFIG.autoScroll,
      sendOnEnter: config.sendOnEnter ?? DEFAULT_CONFIG.sendOnEnter,

      // Header
      showCloseButton: config.showCloseButton ?? DEFAULT_CONFIG.showCloseButton,
      showHeader: config.showHeader ?? DEFAULT_CONFIG.showHeader,
      headerHeight: config.headerHeight ?? DEFAULT_CONFIG.headerHeight,

      // User
      user: {
        id: config.user?.id ?? DEFAULT_CONFIG.user.id,
        name: config.user?.name ?? DEFAULT_CONFIG.user.name,
        email: config.user?.email ?? DEFAULT_CONFIG.user.email,
        avatar: config.user?.avatar ?? DEFAULT_CONFIG.user.avatar
      },

      metadata: config.metadata ?? DEFAULT_CONFIG.metadata,

      // Container
      container: this.resolveContainer(config.container),
      embedded: config.embedded ?? DEFAULT_CONFIG.embedded,

      // Callbacks
      onOpen: config.onOpen ?? DEFAULT_CONFIG.onOpen,
      onClose: config.onClose ?? DEFAULT_CONFIG.onClose,
      onMessageSent: config.onMessageSent ?? DEFAULT_CONFIG.onMessageSent,
      onMessageReceived: config.onMessageReceived ?? DEFAULT_CONFIG.onMessageReceived,
      onError: config.onError ?? DEFAULT_CONFIG.onError
    };
  }

  /**
   * Initialize and render the widget
   */
  init(): void {
    if (this.initialized) {
      console.warn('[ChatWidget] Already initialized');
      return;
    }

    this.loadPersistedHistory();
    this.createContainer();
    this.render();
    this.attachEventListeners();
    this.initialized = true;

    // Show welcome message if configured and no history
    if (this.config.welcomeMessage && this.messages.length === 0) {
      this.addMessage({
        id: generateId(),
        content: this.config.welcomeMessage,
        sender: 'bot',
        timestamp: new Date()
      });
    }

    // Show initial prompts if configured and no history
    if (this.config.initialPrompts.length > 0 && this.messages.length <= 1) {
      this.renderInitialPrompts();
    }

    // Open if configured
    if (this.config.startOpen) {
      this.open();
    }
  }

  /**
   * Render initial prompt suggestions
   */
  private renderInitialPrompts(): void {
    if (!this.messagesEl || this.config.initialPrompts.length === 0) return;

    const promptsContainer = document.createElement('div');
    promptsContainer.className = 'chat-initial-prompts';
    promptsContainer.setAttribute('data-prompts', 'true');

    this.config.initialPrompts.forEach((prompt) => {
      const btn = document.createElement('button');
      btn.className = 'chat-prompt-btn';
      btn.textContent = prompt;
      btn.addEventListener('click', () => this.handlePromptClick(prompt));
      promptsContainer.appendChild(btn);
    });

    this.messagesEl.appendChild(promptsContainer);
    this.scrollToBottom();
  }

  /**
   * Handle initial prompt click
   */
  private handlePromptClick(prompt: string): void {
    // Remove prompts container
    this.hideInitialPrompts();

    // Send the prompt as a message
    const userMessage: ChatMessage = {
      id: generateId(),
      content: prompt,
      sender: 'user',
      timestamp: new Date()
    };
    this.addMessage(userMessage);
    this.config.onMessageSent?.(prompt);
    this.sendToAPI(prompt);
  }

  /**
   * Hide initial prompts
   */
  private hideInitialPrompts(): void {
    const prompts = this.messagesEl?.querySelector('[data-prompts="true"]');
    prompts?.remove();
  }

  /**
   * Load persisted message history
   */
  private loadPersistedHistory(): void {
    if (!this.config.persistHistory) return;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.messages = parsed.map((msg: ChatMessage) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));

        // Restore chat service history
        this.messages.forEach((msg) => {
          const role = msg.sender === 'user' ? 'user' : 'assistant';
          this.chatService.getHistory().push({ role, content: msg.content });
        });
      }
    } catch (e) {
      console.warn('[ChatWidget] Failed to load history:', e);
    }
  }

  /**
   * Persist message history
   */
  private persistHistory(): void {
    if (!this.config.persistHistory) return;

    try {
      const toStore = this.messages.slice(-this.config.maxHistoryLength);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
    } catch (e) {
      console.warn('[ChatWidget] Failed to persist history:', e);
    }
  }

  /**
   * Create the widget container with Shadow DOM
   */
  private createContainer(): void {
    this.container = document.createElement('div');
    this.container.id = 'chat-widget-root';
    this.shadowRoot = this.container.attachShadow({ mode: 'open' });

    // Append to custom container or document.body
    const targetContainer = this.config.container || document.body;
    targetContainer.appendChild(this.container);
  }

  /**
   * Render the widget HTML
   */
  private render(): void {
    if (!this.shadowRoot) return;

    const host = this.shadowRoot.host as HTMLElement;
    host.classList.toggle('theme-dark', this.config.theme === 'dark');
    host.classList.toggle('embedded', this.config.embedded);

    // Create style element with customizations
    const styleEl = document.createElement('style');
    styleEl.textContent = widgetStyles + this.getCustomStyles();

    // Create main container
    const widgetContainer = document.createElement('div');
    const embeddedClass = this.config.embedded ? ' embedded' : '';
    widgetContainer.className = `chat-widget-container position-${this.config.position}${embeddedClass}`;
    widgetContainer.innerHTML = this.getWidgetHTML();

    this.shadowRoot.appendChild(styleEl);
    this.shadowRoot.appendChild(widgetContainer);

    // Cache DOM references
    this.panelEl = this.shadowRoot.querySelector('.chat-panel');
    this.messagesEl = this.shadowRoot.querySelector('.chat-messages');
    this.inputEl = this.shadowRoot.querySelector('.chat-input');
    this.toggleBtn = this.shadowRoot.querySelector('.chat-toggle-btn');
    this.sendBtn = this.shadowRoot.querySelector('.chat-send-btn');

    // Render persisted messages
    this.messages.forEach((msg) => this.renderMessage(msg));
  }

  /**
   * Get custom CSS variable overrides
   */
  private getCustomStyles(): string {
    const c = this.config;
    return `
      :host {
        --chat-primary-color: ${c.primaryColor};
        --chat-primary-hover: ${this.darkenColor(c.primaryColor, 10)};
        --chat-bg-color: ${c.backgroundColor};
        --chat-text-color: ${c.textColor};
        --chat-bot-bg: ${c.botBubbleColor};
        --chat-user-bg: ${c.userBubbleColor};
        --chat-width: ${c.width}px;
        --chat-height: ${c.height}px;
        --chat-button-size: ${c.buttonSize}px;
        --chat-radius: ${c.borderRadius}px;
        --chat-font: ${c.fontFamily};
        --chat-font-size: ${c.fontSize}px;
        --chat-button-offset-x: ${c.buttonOffset.x}px;
        --chat-button-offset-y: ${c.buttonOffset.y}px;
        --chat-header-height: ${c.headerHeight}px;
      }
    `;
  }

  /**
   * Darken a hex color
   */
  private darkenColor(hex: string, percent: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max((num >> 16) - amt, 0);
    const G = Math.max((num >> 8 & 0x00FF) - amt, 0);
    const B = Math.max((num & 0x0000FF) - amt, 0);
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
  }

  /**
   * Get the widget HTML template
   */
  private getWidgetHTML(): string {
    const c = this.config;

    const headerHTML = c.showHeader ? `
      <div class="chat-header">
        <div class="chat-header-content">
          <div class="chat-header-avatar">${c.botAvatar.startsWith('http') || c.botAvatar.startsWith('/') ? `<img src="${c.botAvatar}" alt="${c.botName}">` : c.botAvatar}</div>
          <div class="chat-header-info">
            <h2>${this.escapeHtml(c.title)}</h2>
            <p>${this.escapeHtml(c.subtitle)}</p>
          </div>
        </div>
        <div class="chat-header-actions">
          <button class="chat-expand-btn" aria-label="Expand chat" title="Expand chat">
            ${icons.expand}
          </button>
          <button class="chat-reset-btn" aria-label="Reset chat" title="Reset chat">
            ${icons.reset}
          </button>
          ${c.showCloseButton ? `
            <button class="chat-close-btn" aria-label="Close chat">
              ${c.closeIcon || icons.close}
            </button>
          ` : ''}
        </div>
      </div>
    ` : '';

    const poweredByHTML = '';

    const buttonIconHTML = c.buttonIcon
      ? (c.buttonIcon.startsWith('<') ? c.buttonIcon : `<span class="btn-emoji">${c.buttonIcon}</span>`)
      : icons.chat;

    return `
      <div class="chat-panel">
        ${headerHTML}
        <div class="chat-messages" role="log" aria-live="polite"></div>
        <div class="chat-input-container">
          <textarea
            class="chat-input"
            placeholder="${this.escapeHtml(c.placeholder)}"
            rows="1"
            aria-label="Message input"
          ></textarea>
          <button class="chat-send-btn" aria-label="Send message">
            ${icons.send}
          </button>
        </div>
        ${poweredByHTML}
      </div>
      <button class="chat-toggle-btn" aria-label="Open chat">
        ${buttonIconHTML}
      </button>
    `;
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    if (!this.shadowRoot) return;

    // Toggle button
    this.toggleBtn?.addEventListener('click', () => this.toggle());

    // Close button
    const closeBtn = this.shadowRoot.querySelector('.chat-close-btn');
    closeBtn?.addEventListener('click', () => this.close());

    // Expand button
    const expandBtn = this.shadowRoot.querySelector('.chat-expand-btn');
    expandBtn?.addEventListener('click', () => this.toggleExpand());

    // Reset button
    const resetBtn = this.shadowRoot.querySelector('.chat-reset-btn');
    resetBtn?.addEventListener('click', () => this.clearHistory());

    // Send button
    this.sendBtn?.addEventListener('click', () => this.handleSend());

    // Input keydown
    this.inputEl?.addEventListener('keydown', (e: KeyboardEvent) => {
      if (this.config.sendOnEnter && e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSend();
      }
    });

    // Auto-resize textarea
    this.inputEl?.addEventListener('input', () => {
      if (this.inputEl) {
        this.inputEl.style.height = 'auto';
        this.inputEl.style.height = Math.min(this.inputEl.scrollHeight, 120) + 'px';
      }
    });
  }

  /**
   * Handle sending a message
   */
  private async handleSend(): Promise<void> {
    if (!this.inputEl || this.isLoading) return;

    const text = this.inputEl.value.trim();
    if (!text) return;

    // Hide initial prompts when user sends first message
    this.hideInitialPrompts();

    // Clear input
    this.inputEl.value = '';
    this.inputEl.style.height = 'auto';

    // Add user message
    const userMessage: ChatMessage = {
      id: generateId(),
      content: text,
      sender: 'user',
      timestamp: new Date()
    };
    this.addMessage(userMessage);

    // Callback
    this.config.onMessageSent?.(text);

    // Send to API
    await this.sendToAPI(text);
  }

  /**
   * Send message to backend API
   */
  private async sendToAPI(text: string): Promise<void> {
    this.setLoading(true);
    this.showTypingIndicator();

    try {
      const reply = await this.chatService.sendMessage(text);
      this.hideTypingIndicator();

      const botMessage: ChatMessage = {
        id: generateId(),
        content: reply,
        sender: 'bot',
        timestamp: new Date()
      };
      this.addMessage(botMessage);

      // Play notification sound
      if (this.config.soundEnabled && this.audio) {
        this.audio.play().catch(() => {});
      }

      // Callback
      this.config.onMessageReceived?.(reply);
    } catch (error) {
      this.hideTypingIndicator();

      const errorMessage: ChatMessage = {
        id: generateId(),
        content: this.config.errorMessage,
        sender: 'bot',
        timestamp: new Date()
      };
      this.addMessage(errorMessage);

      // Callback
      if (error instanceof Error) {
        this.config.onError?.(error);
      }
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Add a message to the chat
   */
  private addMessage(message: ChatMessage): void {
    this.messages.push(message);
    this.renderMessage(message);
    this.persistHistory();

    if (this.config.autoScroll) {
      this.scrollToBottom();
    }
  }

  /**
   * Render a single message
   */
  private renderMessage(message: ChatMessage): void {
    if (!this.messagesEl) return;

    const container = document.createElement('div');
    container.className = `chat-message-container ${message.sender}`;
    container.setAttribute('data-id', message.id);

    const c = this.config;
    const showAvatar = message.sender === 'bot' && c.showBotAvatar;

    let html = '';

    if (showAvatar) {
      const avatarContent = c.botAvatar.startsWith('http') || c.botAvatar.startsWith('/')
        ? `<img src="${c.botAvatar}" alt="${c.botName}">`
        : c.botAvatar;
      html += `<div class="chat-message-avatar">${avatarContent}</div>`;
    }

    html += `<div class="chat-message-content">`;

    if (message.sender === 'bot' && c.botName) {
      html += `<span class="chat-message-sender">${this.escapeHtml(c.botName)}</span>`;
    }

    html += `<div class="chat-message ${message.sender}">${parseMarkdown(message.content)}</div>`;

    if (c.showTimestamps) {
      html += `<span class="chat-message-time">${formatTime(message.timestamp)}</span>`;
    }

    html += `</div>`;

    container.innerHTML = html;
    this.messagesEl.appendChild(container);
  }

  /**
   * Show typing indicator
   */
  private showTypingIndicator(): void {
    if (!this.messagesEl) return;

    const container = document.createElement('div');
    container.className = 'chat-message-container bot';
    container.setAttribute('data-typing', 'true');

    let html = '';

    if (this.config.showBotAvatar) {
      const avatarContent = this.config.botAvatar.startsWith('http') || this.config.botAvatar.startsWith('/')
        ? `<img src="${this.config.botAvatar}" alt="">`
        : this.config.botAvatar;
      html += `<div class="chat-message-avatar">${avatarContent}</div>`;
    }

    html += `
      <div class="chat-message-content">
        <div class="typing-indicator">
          <span></span><span></span><span></span>
        </div>
      </div>
    `;

    container.innerHTML = html;
    this.messagesEl.appendChild(container);
    this.scrollToBottom();
  }

  /**
   * Hide typing indicator
   */
  private hideTypingIndicator(): void {
    const indicator = this.messagesEl?.querySelector('[data-typing="true"]');
    indicator?.remove();
  }

  /**
   * Scroll messages to bottom
   */
  private scrollToBottom(): void {
    if (this.messagesEl) {
      requestAnimationFrame(() => {
        if (this.messagesEl) {
          this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
        }
      });
    }
  }

  /**
   * Set loading state
   */
  private setLoading(loading: boolean): void {
    this.isLoading = loading;
    if (this.sendBtn) {
      this.sendBtn.disabled = loading;
    }
    if (this.inputEl) {
      this.inputEl.disabled = loading;
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Toggle widget open/closed
   */
  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the chat panel
   */
  open(): void {
    if (this.isOpen) return;

    this.isOpen = true;
    this.panelEl?.classList.add('open');
    this.toggleBtn?.classList.add('open');

    if (this.config.autoFocus) {
      setTimeout(() => this.inputEl?.focus(), 100);
    }

    this.config.onOpen?.();
  }

  /**
   * Close the chat panel
   */
  close(): void {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.panelEl?.classList.remove('open');
    this.toggleBtn?.classList.remove('open');

    this.config.onClose?.();
  }

  /**
   * Destroy the widget and clean up
   */
  destroy(): void {
    if (!this.initialized) return;

    this.container?.remove();
    this.container = null;
    this.shadowRoot = null;
    this.panelEl = null;
    this.messagesEl = null;
    this.inputEl = null;
    this.toggleBtn = null;
    this.sendBtn = null;
    this.messages = [];
    this.initialized = false;
  }

  /**
   * Programmatically send a message
   */
  sendMessage(text: string): void {
    if (!this.initialized || !text.trim()) return;

    const userMessage: ChatMessage = {
      id: generateId(),
      content: text.trim(),
      sender: 'user',
      timestamp: new Date()
    };
    this.addMessage(userMessage);
    this.config.onMessageSent?.(text.trim());
    this.sendToAPI(text.trim());
  }

  /**
   * Clear conversation history
   */
  clearHistory(): void {
    this.messages = [];
    this.chatService.clearHistory();
    this.chatService.resetSession();

    if (this.messagesEl) {
      this.messagesEl.innerHTML = '';
    }

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}

    // Re-show welcome message if configured
    if (this.config.welcomeMessage) {
      this.addMessage({
        id: generateId(),
        content: this.config.welcomeMessage,
        sender: 'bot',
        timestamp: new Date()
      });
    }

    // Re-show initial prompts if configured
    if (this.config.initialPrompts.length > 0) {
      this.renderInitialPrompts();
    }
  }

  /**
   * Update user info
   */
  setUser(user: ChatWidgetConfig['user']): void {
    if (user) {
      this.config.user = {
        id: user.id ?? this.config.user.id,
        name: user.name ?? this.config.user.name,
        email: user.email ?? this.config.user.email,
        avatar: user.avatar ?? this.config.user.avatar
      };
    }
  }

  /**
   * Toggle expanded/collapsed size
   */
  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
    this.panelEl?.classList.toggle('expanded', this.isExpanded);

    // Update expand button icon
    const expandBtn = this.shadowRoot?.querySelector('.chat-expand-btn');
    if (expandBtn) {
      expandBtn.innerHTML = this.isExpanded ? icons.collapse : icons.expand;
      expandBtn.setAttribute('aria-label', this.isExpanded ? 'Collapse chat' : 'Expand chat');
      expandBtn.setAttribute('title', this.isExpanded ? 'Collapse chat' : 'Expand chat');
    }
  }
}
